import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  GripVertical,
  Plus,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { CompactSingleSelectDropdown, FieldError, RequiredLabel } from "./TeacherMocksShared";
import SelectedQuestionItem from "./SelectedQuestionItem";
import QuestionSelectorModal from "./QuestionSelectorModal";
import TeacherMocksInlineQuestionBank from "./TeacherMocksInlineQuestionBank";
import PublicationSettingsSection from "./PublicationSettingsSection";
import StudentSearchSection from "./StudentSearchSection";
import { defaultForm } from "./teacherMocksConstants";
import {
  formatCreateError,
  haveQuestionChanges,
  managerQuery,
  mapQuestionToSelected,
  mergeTemplateQuestionIds,
  previewText,
  questionTypeTag,
  studentSearchQuery,
  validate,
} from "./teacherMocksHelpers";
import { apiGet } from "../../processes/helpers/api";

export default function CreateMockModal({
  courseOptions,
  isLoadingCourses,
  open,
  onClose,
  onCreate,
  onAutosave,
  onUpdate,
  mode = "create",
  initialValues = defaultForm,
  isLoading = false,
}) {
  const [values, setValues] = useState(defaultForm);
  const [originalQuestions, setOriginalQuestions] = useState(defaultForm.questions);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [draggedQuestionId, setDraggedQuestionId] = useState(null);
  const [dragOverQuestionId, setDragOverQuestionId] = useState(null);
  const [createStep, setCreateStep] = useState(1);
  const autosaveTimerRef = useRef(null);
  const [publicationLevels, setPublicationLevels] = useState([]);
  const [isLoadingPublicationLevels, setIsLoadingPublicationLevels] = useState(false);
  const [courseStudentsCount, setCourseStudentsCount] = useState(null);
  const isViewMode = mode === "view";
  const fieldsDisabled = isViewMode || isLoading;
  const errors = useMemo(() => validate(values), [values]);
  const isValid = Object.keys(errors).length === 0;
  const hasQuestionUpdates = useMemo(
    () => isViewMode && haveQuestionChanges(values.questions, originalQuestions),
    [isViewMode, originalQuestions, values.questions]
  );
  const hasSelectedCourseOption = values.courseId &&
    courseOptions.some((item) => String(item.id) === String(values.courseId));

  function clearDragState() {
    setDraggedQuestionId(null);
    setDragOverQuestionId(null);
  }

  useEffect(() => {
    if (!open) return;
    setValues(initialValues);
    setTouched({});
    setSubmitted(false);
    setSubmitError("");
    setIsSubmitting(false);
    setIsSelectorOpen(false);
    setCreateStep(1);
    clearDragState();
    if (mode === "view") setOriginalQuestions(initialValues.questions);
  }, [initialValues, mode, open]);

  useEffect(() => () => clearTimeout(autosaveTimerRef.current), []);

  useEffect(() => {
    clearTimeout(autosaveTimerRef.current);
    if (!open || isViewMode || isLoading || isSubmitting || !onAutosave) return undefined;
    if (!values.courseId || values.questions.length === 0) return undefined;

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const draft = await onAutosave(values);
        if (!draft?.id) return;
        setValues((current) => {
          const questions = mergeTemplateQuestionIds(current.questions, draft);
          if (current.id === draft.id && current.draft === true && questions === current.questions) return current;
          return { ...current, id: draft.id, draft: true, questions };
        });
      } catch {
        // Autosave is best-effort; explicit publish still shows submit errors.
      }
    }, 800);

    return () => clearTimeout(autosaveTimerRef.current);
  }, [isLoading, isSubmitting, isViewMode, onAutosave, open, values]);

  useEffect(() => {
    if (!open || !values.courseId || isViewMode) {
      setPublicationLevels([]);
      setCourseStudentsCount(null);
      return undefined;
    }

    let isMounted = true;

    async function loadPublicationLevels() {
      try {
        setIsLoadingPublicationLevels(true);
        const levelsQuery = managerQuery({
          "q[course_id_eq]": values.courseId,
          "q[sublevel_id_null]": true,
          "q[s]": "position asc",
          paginated: true,
          page: 1,
          per_page: 200,
        });
        const studentsQuery = studentSearchQuery({ search: "", page: 1, courseId: values.courseId });
        const [levelsResponse, studentsResponse] = await Promise.all([
          apiGet(`/api/v2/manager/levels?${levelsQuery}`),
          apiGet(`/api/v2/manager/users?${studentsQuery}`),
        ]);
        if (!isMounted) return;
        setPublicationLevels(levelsResponse.body?.data || []);
        setCourseStudentsCount(studentsResponse.body?.pagination?.totalItems ?? null);
      } finally {
        if (isMounted) setIsLoadingPublicationLevels(false);
      }
    }

    loadPublicationLevels();

    return () => {
      isMounted = false;
    };
  }, [isViewMode, open, values.courseId]);

  if (!open) return null;

  const showError = (field) => (touched[field] || submitted) && errors[field];
  const errorId = (field) => `teacher-mocks-${field}-error`;

  const updateField = (field) => (event) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
  };

  const updateCheckedField = (field) => (event) => {
    setValues((current) => ({ ...current, [field]: event.target.checked }));
  };

  const touchField = (field) => () => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const handleToggleSendToAll = () => {
    setValues((current) => ({
      ...current,
      sendToAll: !current.sendToAll,
      ...(current.sendToAll ? {} : { studentIds: [], studentNames: {} }),
    }));
  };

  const handleAvailabilityModeChange = (availabilityMode) => {
    setValues((current) => ({
      ...current,
      availabilityMode,
      sendToAll: availabilityMode === "all",
      ...(availabilityMode !== "specific" ? { studentIds: [], studentNames: {} } : {}),
      ...(availabilityMode !== "module_completion" ? { triggerLevelId: "", triggerLevelName: "" } : {}),
    }));
  };

  const handleAddStudent = (student) => {
    setValues((current) => {
      if (current.studentIds.includes(student.id)) return current;
      return {
        ...current,
        studentIds: [...current.studentIds, student.id],
        studentNames: { ...current.studentNames, [student.id]: student.name },
      };
    });
  };

  const handleUpdateStudents = (ids, names) => {
    setValues((current) => ({ ...current, studentIds: ids, studentNames: names }));
  };

  const handleRemoveStudent = (studentId) => {
    setValues((current) => {
      const studentIds = current.studentIds.filter((id) => id !== studentId);
      const studentNames = { ...current.studentNames };
      delete studentNames[studentId];
      return { ...current, studentIds, studentNames };
    });
  };

  const handleTriggerLevelChange = (levelId) => {
    const level = publicationLevels.find((item) => String(item.id) === String(levelId));
    setValues((current) => ({
      ...current,
      triggerLevelId: levelId,
      triggerLevelName: level?.name || "",
    }));
  };

  const handleToggleNotify = () => {
    setValues((current) => ({ ...current, notifyStudents: !current.notifyStudents }));
  };

  const resetForm = () => {
    clearTimeout(autosaveTimerRef.current);
    setValues(initialValues);
    setTouched({});
    setSubmitted(false);
    setSubmitError("");
    setIsSubmitting(false);
    setIsSelectorOpen(false);
    setCreateStep(1);
    clearDragState();
  };

  const handleCourseChange = (event) => {
    updateField("courseId")(event);
    setValues((current) => ({
      ...current,
      courseName: courseOptions.find((item) => String(item.id) === String(event.target.value))?.name || "",
      questions: [],
      studentIds: [],
      studentNames: {},
      triggerLevelId: "",
      triggerLevelName: "",
    }));
  };

  const closeModal = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleAddQuestions = (questions) => {
    setValues((current) => {
      const currentIds = new Set(current.questions.map((question) => Number(question.questionId)));
      const newQuestions = questions
        .filter((question) => !currentIds.has(Number(question.id)))
        .map((question, index) => mapQuestionToSelected(question, current.questions.length + index + 1));

      return { ...current, questions: [...current.questions, ...newQuestions] };
    });
  };

  const handleAddQuestion = (question) => handleAddQuestions([question]);

  const handleClearQuestions = () => {
    setValues((current) => ({ ...current, questions: [] }));
  };

  const handleRemoveQuestion = (questionId) => {
    setValues((current) => ({
      ...current,
      questions: current.questions
        .filter((question) => Number(question.questionId) !== Number(questionId))
        .map((question, index) => ({ ...question, position: index + 1 })),
    }));
  };

  const handleReorderQuestion = (fromQuestionId, toQuestionId) => {
    if (!fromQuestionId || !toQuestionId || Number(fromQuestionId) === Number(toQuestionId)) return;

    setValues((current) => {
      const fromIndex = current.questions.findIndex((question) => Number(question.questionId) === Number(fromQuestionId));
      const toIndex = current.questions.findIndex((question) => Number(question.questionId) === Number(toQuestionId));
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return current;

      const questions = [...current.questions];
      const [movedQuestion] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, movedQuestion);

      return {
        ...current,
        questions: questions.map((question, index) => ({ ...question, position: index + 1 })),
      };
    });
  };

  const handleUpdate = async () => {
    if (!isViewMode || !onUpdate || !hasQuestionUpdates) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await onUpdate(values, originalQuestions);
      setOriginalQuestions(values.questions);
      setIsSubmitting(false);
    } catch (error) {
      setSubmitError(formatCreateError(error));
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (isViewMode) return;
    if (createStep !== 2) return;

    setSubmitted(true);
    if (!isValid) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await onCreate(values);
      resetForm();
      onClose();
    } catch (error) {
      setSubmitError(formatCreateError(error));
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const totalSelectedQuestions = values.questions.length;
  const hasMinimumQuestions = totalSelectedQuestions >= 5;

  if (!isViewMode) {
    return (
      <div className="teacher-mocks-create-overlay">
        <form className="teacher-mocks-create-screen" onSubmit={handleSubmit}>
          <header className="teacher-mocks-create-header">
            <div className="teacher-mocks-create-title">
              <button type="button" aria-label="Voltar" onClick={closeModal} disabled={isSubmitting}>
                <ChevronLeft size={20} aria-hidden="true" />
              </button>
              <h2>Criar simulado</h2>
              {values.courseName && <span>· {values.courseName}</span>}
            </div>

            <div className="teacher-mocks-create-steps" aria-label="Etapas de criação">
              <button
                type="button"
                className={createStep === 1 ? "active" : "complete"}
                onClick={() => setCreateStep(1)}
                disabled={isSubmitting}
              >
                <strong>1</strong>
                Seleção de questões
              </button>
              <i aria-hidden="true" />
              <button
                type="button"
                className={createStep === 2 ? "active" : ""}
                onClick={() => setCreateStep(2)}
                disabled={!hasMinimumQuestions || isSubmitting}
              >
                <strong>2</strong>
                Publicação
              </button>
            </div>

            <div className="teacher-mocks-create-actions">
              {createStep === 1 ? (
                <button
                  type="button"
                  className="teacher-mocks-create-next"
                  disabled={!hasMinimumQuestions}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setValues((current) => {
                      if (current.name.trim()) return current;
                      const dateStr = new Date().toLocaleDateString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                      });
                      return { ...current, name: `${current.courseName} — ${dateStr}` };
                    });
                    setCreateStep(2);
                  }}
                >
                  Próximo · {totalSelectedQuestions} questões
                </button>
              ) : (
                <button
                  type="button"
                  className="teacher-mocks-create-next"
                  disabled={!isValid || isSubmitting}
                  onClick={handlePublish}
                >
                  <Send size={14} aria-hidden="true" />
                  {isSubmitting ? "Criando..." : "Publicar simulado"}
                </button>
              )}
              <button type="button" className="teacher-mocks-create-exit" onClick={closeModal} disabled={isSubmitting}>
                Sair
              </button>
            </div>
          </header>

          {createStep === 1 ? (
            <main className="teacher-mocks-create-main">
              <section className="teacher-mocks-create-workspace">
                <div className="teacher-mocks-create-course">
                  <CompactSingleSelectDropdown
                    label={<>Curso <span aria-hidden="true">*</span></>}
                    value={values.courseId ? String(values.courseId) : ""}
                    onChange={(val) => handleCourseChange({ target: { value: val } })}
                    onBlur={touchField("courseId")}
                    disabled={isLoadingCourses || courseOptions.length === 0}
                    options={[
                      ...(!hasSelectedCourseOption && values.courseId
                        ? [{ value: String(values.courseId), label: values.courseName || "Curso selecionado" }]
                        : []),
                      ...courseOptions.map((item) => ({ value: String(item.id), label: item.name })),
                    ]}
                    placeholder={isLoadingCourses ? "Carregando cursos..." : "Selecione um curso para começar..."}
                  />
                  {showError("courseId") && <FieldError id={errorId("courseId")}>{errors.courseId}</FieldError>}
                </div>

                <div className="teacher-mocks-create-selection">
                  {!values.courseId ? (
                    <div className="teacher-mocks-create-empty">
                      <span>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M3.99996 19.5001C3.99996 18.8371 4.26335 18.2012 4.73219 17.7323C5.20104 17.2635 5.83692 17.0001 6.49996 17.0001H20" stroke="#0369A1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6.49996 2.00004H20V22.0001H6.49996C5.83692 22.0001 5.20104 21.7367 4.73219 21.2678C4.26335 20.799 3.99996 20.1631 3.99996 19.5001V4.50005C3.99996 3.837 4.26335 3.20112 4.73219 2.73228C5.20104 2.26343 5.83692 2.00004 6.49996 2.00004Z" stroke="#0369A1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <h3>Selecione um curso</h3>
                      <p>Escolha o curso acima para visualizar e selecionar as questões do banco.</p>
                    </div>
                  ) : (
                    <TeacherMocksInlineQuestionBank
                      courseId={Number(values.courseId)}
                      selectedQuestions={values.questions}
                      onAddQuestion={handleAddQuestion}
                      onRemoveQuestion={handleRemoveQuestion}
                      onAddQuestions={handleAddQuestions}
                      onClearQuestions={handleClearQuestions}
                    />
                  )}
                </div>
              </section>

              <aside className="teacher-mocks-create-preview">
                <header>
                  <h3>Preview do simulado</h3>
                  <span>{totalSelectedQuestions} questões</span>
                </header>
                <div className="teacher-mocks-create-preview-body">
                  <div className="teacher-mocks-create-preview-panel">
                    {!hasMinimumQuestions && totalSelectedQuestions > 0 && (
                      <div className="teacher-mocks-create-minimum-alert">Mínimo de 5 questões</div>
                    )}
                    {values.questions.length === 0 ? (
                      <div className="teacher-mocks-create-preview-empty">
                        <span>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M6.66671 4.99991H17.5" stroke="#94A3B8" strokeWidth="1.25" strokeLinecap="round"/>
                            <path d="M6.66671 10.0001H17.5" stroke="#94A3B8" strokeWidth="1.25" strokeLinecap="round"/>
                            <path d="M6.66671 15H17.5" stroke="#94A3B8" strokeWidth="1.25" strokeLinecap="round"/>
                            <path d="M2.49995 4.99991H2.50829" stroke="#94A3B8" strokeWidth="1.66666" strokeLinecap="round"/>
                            <path d="M2.49995 10.0001H2.50829" stroke="#94A3B8" strokeWidth="1.66666" strokeLinecap="round"/>
                            <path d="M2.49995 15H2.50829" stroke="#94A3B8" strokeWidth="1.66666" strokeLinecap="round"/>
                          </svg>
                        </span>
                        <strong>Nenhuma questão</strong>
                        <p>Selecione questões à esquerda para montar o simulado.</p>
                      </div>
                    ) : (
                      <ol>
                        {values.questions.map((question, index) => {
                          const typeTag = questionTypeTag(question);
                          const questionText = previewText(
                            question.context || question.firstStemContent || `Questão #${question.questionId}`
                          );

                          return (
                            <li
                              key={question.questionId}
                              draggable={!isSubmitting && !isLoading}
                              onDragStart={(event) => {
                                event.dataTransfer.effectAllowed = "move";
                                event.dataTransfer.setData("text/plain", String(question.questionId));
                                setDraggedQuestionId(question.questionId);
                              }}
                              onDragOver={(event) => {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = "move";
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                const fromQuestionId = event.dataTransfer.getData("text/plain") || draggedQuestionId;
                                handleReorderQuestion(fromQuestionId, question.questionId);
                                clearDragState();
                              }}
                              onDragEnd={clearDragState}
                            >
                              <GripVertical size={14} color="#94a3b8" aria-hidden="true" />
                              <span className="teacher-mocks-create-preview-position">{index + 1}</span>
                              <strong>Q-{String(question.questionId).padStart(4, "0")}</strong>
                              <div>
                                <p>{questionText}</p>
                              </div>
                              <span className={`teacher-mocks-create-preview-tag ${typeTag.className}`}>
                                {typeTag.label}
                              </span>
                              <button type="button" onClick={() => handleRemoveQuestion(question.questionId)} aria-label="Remover questão">
                                <Trash2 size={12} color="#ef4444" aria-hidden="true" />
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                </div>
              </aside>
            </main>
          ) : (
            <main className="teacher-mocks-create-main teacher-mocks-create-publication">
              <section className="teacher-mocks-create-workspace">
                <div className="teacher-mocks-create-publication-card">
                  <button
                    type="button"
                    className="teacher-mocks-publication-back"
                    onClick={() => setCreateStep(1)}
                    disabled={isSubmitting}
                  >
                    <ArrowLeft size={16} aria-hidden="true" />
                    Voltar para seleção de questões
                  </button>

                  <section className="teacher-mocks-publication-section">
                    <div className="teacher-mocks-publication-section-title">
                      <h3>Informações do simulado</h3>
                    </div>

                    <div className="teacher-mocks-publication-name">
                      <RequiredLabel>Nome do simulado</RequiredLabel>
                      <input
                        value={values.name}
                        onChange={updateField("name")}
                        onBlur={touchField("name")}
                        placeholder="Nome gerado automaticamente..."
                        aria-invalid={!!showError("name")}
                        aria-describedby={showError("name") ? errorId("name") : undefined}
                        disabled={isSubmitting}
                      />
                      {showError("name") && <FieldError id={errorId("name")}>{errors.name}</FieldError>}
                    </div>

                    <div className="teacher-mocks-publication-metrics">
                      <div>
                        <span>Total de questões</span>
                        <strong>{totalSelectedQuestions}</strong>
                      </div>
                      <div>
                        <span>Curso</span>
                        <strong>{values.courseName || "Curso selecionado"}</strong>
                      </div>
                    </div>
                  </section>

                  <PublicationSettingsSection
                    values={values}
                    errors={errors}
                    showError={showError}
                    errorId={errorId}
                    isSubmitting={isSubmitting}
                    publicationLevels={publicationLevels}
                    isLoadingPublicationLevels={isLoadingPublicationLevels}
                    courseStudentsCount={courseStudentsCount}
                    onAvailabilityModeChange={handleAvailabilityModeChange}
                    onAddStudent={handleAddStudent}
                    onRemoveStudent={handleRemoveStudent}
                    onUpdateStudents={handleUpdateStudents}
                    onTriggerLevelChange={handleTriggerLevelChange}
                    onToggleNotify={handleToggleNotify}
                  />

                  {submitError && <div className="teacher-mocks-submit-error">{submitError}</div>}
                </div>
              </section>
            </main>
          )}
        </form>

        {isSelectorOpen && (
          <QuestionSelectorModal
            courseId={Number(values.courseId)}
            excludeQuestionIds={values.questions.map((question) => question.questionId)}
            onAddQuestions={handleAddQuestions}
            onClose={() => setIsSelectorOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="teacher-mocks-modal-overlay" onMouseDown={closeModal}>
      <form className="teacher-mocks-modal" onMouseDown={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
        <div className="teacher-mocks-modal-header">
          <div>
            <h2>{isViewMode ? "Visualizar Simulado" : "Criar Simulado"}</h2>
            <p>{isViewMode ? "Informações do simulado" : "Configurações iniciais do simulado"}</p>
          </div>
          <button
            type="button"
            className="teacher-mocks-icon-button"
            onClick={closeModal}
            aria-label="Fechar"
            disabled={isSubmitting}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="teacher-mocks-modal-body teacher-mocks-manager-grid">
          {isLoading ? (
            <div className="teacher-mocks-modal-loading">
              Carregando simulado...
            </div>
          ) : null}
          <section className="teacher-mocks-manager-card teacher-mocks-manager-info">
            <header>
              <h3>Informações</h3>
            </header>
            <div className="teacher-mocks-manager-card-content">
              <div className="teacher-mocks-field">
                <RequiredLabel>Nome</RequiredLabel>
                <input
                  value={values.name}
                  onChange={updateField("name")}
                  onBlur={touchField("name")}
                  placeholder="Digite o nome do simulado"
                  aria-invalid={!!showError("name")}
                  aria-describedby={showError("name") ? errorId("name") : undefined}
                  disabled={fieldsDisabled}
                />
                {showError("name") && <FieldError id={errorId("name")}>{errors.name}</FieldError>}
              </div>

              <div className="teacher-mocks-field">
                <CompactSingleSelectDropdown
                  label={<>Curso <span aria-hidden="true">*</span></>}
                  value={values.courseId ? String(values.courseId) : ""}
                  onChange={(val) => {
                    updateField("courseId")({ target: { value: val } });
                    setValues((current) => ({ ...current, questions: [] }));
                  }}
                  onBlur={touchField("courseId")}
                  disabled={fieldsDisabled || isLoadingCourses || courseOptions.length === 0}
                  options={[
                    ...(!hasSelectedCourseOption && values.courseId
                      ? [{ value: String(values.courseId), label: values.courseName || "Curso selecionado" }]
                      : []),
                    ...courseOptions.map((item) => ({ value: String(item.id), label: item.name })),
                  ]}
                  placeholder={isLoadingCourses ? "Carregando cursos..." : "Selecione um curso"}
                />
                {showError("courseId") && <FieldError id={errorId("courseId")}>{errors.courseId}</FieldError>}
              </div>

              <label className="teacher-mocks-manager-checkbox">
                <input
                  type="checkbox"
                  checked={values.enabled}
                  onChange={updateCheckedField("enabled")}
                  disabled={fieldsDisabled}
                />
                <span>
                  <strong>Habilitado</strong>
                  <small>Quando desabilitado, o simulado não será visível</small>
                </span>
              </label>

              <hr className="teacher-mocks-info-divider" />

              <StudentSearchSection
                values={values}
                isViewMode={isViewMode}
                onToggleSendToAll={handleToggleSendToAll}
                onAddStudent={handleAddStudent}
                onUpdateStudents={handleUpdateStudents}
              />
            </div>
          </section>

          <section className="teacher-mocks-manager-card teacher-mocks-manager-questions">
            <header>
              <h3>
                Questões (
                {values.questions.reduce((sum, question) => sum + (question.stemsCount || 1), 0)}
                )
              </h3>
              <button
                type="button"
                className="teacher-mocks-manager-add"
                disabled={!values.courseId || isSubmitting || isLoading}
                onClick={() => setIsSelectorOpen(true)}
              >
                <Plus size={14} aria-hidden="true" />
                Adicionar Questões
              </button>
            </header>

            <div className="teacher-mocks-manager-card-content">
              {values.questions.length === 0 ? (
                <div className="teacher-mocks-manager-empty">
                  <p>Nenhuma questão adicionada ao simulado.</p>
                  <button
                    type="button"
                    disabled={!values.courseId || isSubmitting || isLoading}
                    onClick={() => setIsSelectorOpen(true)}
                  >
                    <Plus size={14} aria-hidden="true" />
                    Adicionar Questões
                  </button>
                  {!values.courseId && <span>Selecione um curso primeiro</span>}
                </div>
              ) : (
                <div className="teacher-mocks-selected-list">
                  {values.questions.map((question, index) => (
                    <SelectedQuestionItem
                      key={question.questionId}
                      question={question}
                      position={index + 1}
                      onRemove={handleRemoveQuestion}
                      canDrag={!isSubmitting && !isLoading}
                      dragging={Number(draggedQuestionId) === Number(question.questionId)}
                      dragOver={Number(dragOverQuestionId) === Number(question.questionId)}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", String(question.questionId));
                        setDraggedQuestionId(question.questionId);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        setDragOverQuestionId(question.questionId);
                      }}
                      onDragLeave={() => {
                        setDragOverQuestionId((current) =>
                          Number(current) === Number(question.questionId) ? null : current
                        );
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        const fromQuestionId = event.dataTransfer.getData("text/plain") || draggedQuestionId;
                        handleReorderQuestion(fromQuestionId, question.questionId);
                        clearDragState();
                      }}
                      onDragEnd={clearDragState}
                    />
                  ))}
                </div>
              )}
              {showError("questions") && <FieldError id={errorId("questions")}>{errors.questions}</FieldError>}
            </div>
          </section>

          {submitError && <div className="teacher-mocks-submit-error">{submitError}</div>}
        </div>

        <div className="teacher-mocks-modal-footer">
          <button type="button" className="teacher-mocks-secondary-button" onClick={closeModal} disabled={isSubmitting}>
            {isViewMode ? "Fechar" : "Cancelar"}
          </button>
          {isViewMode ? (
            <button
              type="button"
              className="teacher-mocks-primary-button"
              disabled={!hasQuestionUpdates || isSubmitting || isLoading}
              onClick={handleUpdate}
            >
              <Check size={15} aria-hidden="true" />
              {isSubmitting ? "Salvando..." : "Salvar alterações"}
            </button>
          ) : (
            <button type="submit" className="teacher-mocks-primary-button" disabled={!isValid || isSubmitting}>
              <Check size={15} aria-hidden="true" />
              {isSubmitting ? "Criando..." : "Criar simulado"}
            </button>
          )}
        </div>
      </form>

      {isSelectorOpen && (
        <QuestionSelectorModal
          courseId={Number(values.courseId)}
          excludeQuestionIds={values.questions.map((question) => question.questionId)}
          onAddQuestions={handleAddQuestions}
          onClose={() => setIsSelectorOpen(false)}
        />
      )}
    </div>
  );
}
